import { useEffect, useMemo, useState } from "react";
import Indicator from "../../../../../components/base/Indicator";
import { useHub } from "../../../../../core/Global";
import { WalletDateRange } from "../WalletDateFilter";
import { ChartSeries, num, pct, UsageAllowanceCards, UsageDailyChart, UsagePeriod, UsageRow, UsageStatCard, useUsageHeader } from "./UsageCommon";


type Channel = { total: number; inbound: number; outbound: number }

type Day = { day: string; sms: number; whatsApp: number; telegram: number; app: number; internal: number }

type MessagesUsageData = {
    start: string
    end: string
    report: {
        sms: Channel
        whatsApp: Channel
        telegram: Channel
        app: Channel
        internal: Channel
        smsFailed: number
        smsWithMedia: number
        smsForwards: number
        days: Day[]
    }
    billable: number
    fromAllowance: number
    fromWallet: { count: number; amount: number }
    period?: UsagePeriod | null
}


export default function MessagesUsage() {

    const hub = useHub()

    const [range, rangeSet] = useState<WalletDateRange>()
    const [data, dataSet] = useState<MessagesUsageData>()
    const [fetching, fetchingSet] = useState<boolean>(false)

    const mobileFilter = useUsageHeader("Messages", "usage-messages-date", rangeSet)

    useEffect(() => {
        if (!range) return
        fetchingSet(true)
        hub.Get(`/accounts/profile/usage/messages?start=${range.start}&end=${range.end}`, res => {
            if (res?.success && res.data) dataSet(res.data)
            fetchingSet(false)
        })
    }, [range?.start, range?.end])

    const r = data?.report

    const chart = useMemo(() => {
        const days = r?.days ?? []
        const series: ChartSeries[] = [
            { name: "SMS", data: days.map(d => d.sms), color: "--bs-primary" },
            { name: "WhatsApp", data: days.map(d => d.whatsApp), color: "--bs-success" },
            { name: "Telegram", data: days.map(d => d.telegram), color: "--bs-info" },
            { name: "In-App", data: days.map(d => d.app), color: "--bs-warning" },
        ]
        return { days: days.map(d => d.day), series }
    }, [r])

    const total = (r?.sms.total ?? 0) + (r?.whatsApp.total ?? 0) + (r?.telegram.total ?? 0) + (r?.app.total ?? 0)
    const smsOut = r?.sms.outbound ?? 0
    const delivered = Math.max(0, smsOut - (r?.smsFailed ?? 0))
    const caption = range?.label ? `In ${range.label}` : "In selected period"

    return (
        <div>
            <div className="px-9 py-5">
                {mobileFilter}
                {
                    !data ?
                        <div className="text-center py-15"><Indicator /></div>
                        :
                        <div style={{ opacity: fetching ? .6 : 1, transition: "opacity .2s" }}>

                            <div className="row g-5 mb-5">
                                <div className="col-sm-6 col-xl-3">
                                    <UsageStatCard title="SMS" icon="message-sms" color="primary"
                                        value={num(r!.sms.total)} caption={caption}>
                                        <UsageRow label="Sent" value={num(r!.sms.outbound)} />
                                        <UsageRow label="Received" value={num(r!.sms.inbound)} />
                                    </UsageStatCard>
                                </div>
                                <div className="col-sm-6 col-xl-3">
                                    <UsageStatCard title="WhatsApp" icon="whatsapp" brands color="success"
                                        value={num(r!.whatsApp.total)} caption={caption}>
                                        <UsageRow label="Sent" value={num(r!.whatsApp.outbound)} />
                                        <UsageRow label="Received" value={num(r!.whatsApp.inbound)} />
                                    </UsageStatCard>
                                </div>
                                <div className="col-sm-6 col-xl-3">
                                    <UsageStatCard title="Telegram" icon="telegram" brands color="info"
                                        value={num(r!.telegram.total)} caption={caption}>
                                        <UsageRow label="Sent" value={num(r!.telegram.outbound)} />
                                        <UsageRow label="Received" value={num(r!.telegram.inbound)} />
                                    </UsageStatCard>
                                </div>
                                <div className="col-sm-6 col-xl-3">
                                    <UsageStatCard title="In-App" icon="mobile-screen-button" color="warning"
                                        value={num(r!.app.total)} caption={caption}>
                                        <UsageRow label="Sent to technicians" value={num(r!.app.outbound)} />
                                        <UsageRow label="From technicians" value={num(r!.app.inbound)} />
                                    </UsageStatCard>
                                </div>
                            </div>

                            <UsageAllowanceCards
                                unit="messages"
                                fromAllowance={data.fromAllowance}
                                fromWallet={data.fromWallet.count}
                                walletAmount={data.fromWallet.amount}
                                period={data.period}
                                rangeLabel={range?.label} />

                            <div className="row g-5 mb-5">
                                <div className="col-md-6">
                                    <UsageStatCard title="SMS Delivery" icon="circle-exclamation" color="danger"
                                        value={`${pct(delivered, smsOut)}%`} caption="Of sent SMS were accepted by the carrier">
                                        <UsageRow label="Failed / undelivered" value={num(r!.smsFailed)} />
                                        <UsageRow label="With picture or file (MMS)" value={num(r!.smsWithMedia)} />
                                        <UsageRow label="Forwarded copies" value={num(r!.smsForwards)} />
                                        <UsageRow label="Billable SMS (incl. forwards)" value={num(data.billable)} />
                                    </UsageStatCard>
                                </div>
                                <div className="col-md-6">
                                    <UsageStatCard title="All Channels" icon="chart-pie" color="primary"
                                        value={num(total)} caption={caption}>
                                        <UsageRow dot="--bs-primary" label="SMS" value={`${num(r!.sms.total)} · ${pct(r!.sms.total, total)}%`} />
                                        <UsageRow dot="--bs-success" label="WhatsApp" value={`${num(r!.whatsApp.total)} · ${pct(r!.whatsApp.total, total)}%`} />
                                        <UsageRow dot="--bs-info" label="Telegram" value={`${num(r!.telegram.total)} · ${pct(r!.telegram.total, total)}%`} />
                                        <UsageRow dot="--bs-warning" label="In-App" value={`${num(r!.app.total)} · ${pct(r!.app.total, total)}%`} />
                                        {
                                            r!.internal.total > 0 &&
                                            <UsageRow label="Internal group chats (not billed)" value={num(r!.internal.total)} />
                                        }
                                    </UsageStatCard>
                                </div>
                            </div>

                            <UsageDailyChart
                                title="Messages per Day"
                                subtitle={`${num(total)} messages${range?.label ? ` · ${range.label}` : ""}`}
                                days={chart.days}
                                series={chart.series}
                                stacked />
                        </div>
                }
            </div>
        </div>
    )
}
